import { useState } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle, 
  XCircle, 
  ExternalLink, 
  Loader2,
  FileImage,
  AlertTriangle,
  Clock,
  User,
  Mail,
  Phone,
  Calendar,
  DollarSign,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Payment, formatCurrency } from '@/lib/payments-service';

interface ZelleVerificationDialogProps {
  payment: Payment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerified: () => void;
}

const ZelleVerificationDialog = ({
  payment,
  open,
  onOpenChange,
  onVerified,
}: ZelleVerificationDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  // Fetch receipt URL when dialog opens
  const fetchReceiptUrl = async () => {
    if (!payment?.payment_evidence_url) return;
    
    setLoadingReceipt(true);
    try {
      const { data, error } = await supabase.storage
        .from('payment-receipts')
        .createSignedUrl(payment.payment_evidence_url, 3600); // 1 hour expiry

      if (error) throw error;
      setReceiptUrl(data.signedUrl);
    } catch (error) {
      console.error('Error fetching receipt:', error);
      toast({
        title: 'Error',
        description: 'Could not load payment receipt',
        variant: 'destructive',
      });
    } finally {
      setLoadingReceipt(false);
    }
  };

  // Approve Zelle payment
  const handleApprove = async () => {
    if (!payment) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('bookings')
        .update({
          payment_status: 'paid',
          payment_date: new Date().toISOString(),
          payment_notes: 'Zelle payment verified by admin',
        })
        .eq('id', payment.id);

      if (error) throw error;

      toast({
        title: 'Payment Approved',
        description: `Zelle payment for ${payment.full_name} has been verified.`,
      });

      onVerified();
      onOpenChange(false);
    } catch (error) {
      console.error('Error approving payment:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve payment',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Reject Zelle payment
  const handleReject = async () => {
    if (!payment || !rejectReason.trim()) {
      toast({
        title: 'Rejection Reason Required',
        description: 'Please provide a reason for rejecting this payment.',
        variant: 'destructive',
      });
      return;
    }
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('bookings')
        .update({
          payment_status: 'failed',
          payment_notes: `Zelle payment rejected: ${rejectReason}`,
        })
        .eq('id', payment.id);

      if (error) throw error;

      toast({
        title: 'Payment Rejected',
        description: `Zelle payment for ${payment.full_name} has been rejected.`,
        variant: 'destructive',
      });

      onVerified();
      onOpenChange(false);
      setRejectReason('');
      setShowRejectForm(false);
    } catch (error) {
      console.error('Error rejecting payment:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject payment',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset state when dialog closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setReceiptUrl(null);
      setRejectReason('');
      setShowRejectForm(false);
    } else if (payment?.payment_evidence_url) {
      fetchReceiptUrl();
    }
    onOpenChange(open);
  };

  if (!payment) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Verify Zelle Payment
          </DialogTitle>
          <DialogDescription>
            Review the payment details and receipt before approving or rejecting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                Customer
              </div>
              <p className="font-medium">{payment.full_name}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="h-4 w-4" />
                Email
              </div>
              <p className="font-medium">{payment.email}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-4 w-4" />
                Phone
              </div>
              <p className="font-medium">{payment.phone}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                Booking Date
              </div>
              <p className="font-medium">
                {format(new Date(payment.created_at), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
          </div>

          <Separator />

          {/* Payment Details */}
          <div className="space-y-4">
            <h4 className="font-semibold">Payment Details</h4>
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <DollarSign className="h-4 w-4" />
                  Amount
                </div>
                <p className="text-xl font-bold text-primary">
                  {formatCurrency(payment.payment_amount, payment.payment_currency || 'USD')}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Service</p>
                <p className="font-medium">{payment.service}</p>
              </div>
              <div className="space-y-1 col-span-2">
                <p className="text-sm text-muted-foreground">Zelle Confirmation #</p>
                <Badge variant="secondary" className="font-mono text-base">
                  {payment.zelle_confirmation || 'Not provided'}
                </Badge>
              </div>
            </div>
          </div>

          <Separator />

          {/* Receipt Image */}
          <div className="space-y-4">
            <h4 className="font-semibold flex items-center gap-2">
              <FileImage className="h-4 w-4" />
              Payment Receipt
            </h4>
            
            {!payment.payment_evidence_url ? (
              <div className="p-8 bg-muted/50 rounded-lg text-center">
                <AlertTriangle className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                <p className="text-muted-foreground">No receipt uploaded</p>
              </div>
            ) : loadingReceipt ? (
              <div className="p-8 bg-muted/50 rounded-lg text-center">
                <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
                <p className="text-muted-foreground mt-2">Loading receipt...</p>
              </div>
            ) : receiptUrl ? (
              <div className="space-y-2">
                <div className="border rounded-lg overflow-hidden bg-muted/20">
                  <img 
                    src={receiptUrl} 
                    alt="Payment receipt" 
                    className="max-w-full max-h-96 mx-auto object-contain"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2"
                  onClick={() => window.open(receiptUrl, '_blank')}
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Full Size
                </Button>
              </div>
            ) : (
              <div className="p-8 bg-muted/50 rounded-lg text-center">
                <AlertTriangle className="h-8 w-8 mx-auto text-destructive mb-2" />
                <p className="text-muted-foreground">Failed to load receipt</p>
                <Button variant="link" onClick={fetchReceiptUrl} className="mt-2">
                  Retry
                </Button>
              </div>
            )}
          </div>

          {/* Reject Form */}
          {showRejectForm && (
            <>
              <Separator />
              <div className="space-y-3">
                <Label htmlFor="reject-reason" className="text-destructive font-semibold">
                  Rejection Reason *
                </Label>
                <Textarea
                  id="reject-reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why this payment is being rejected..."
                  className="min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground">
                  This will be recorded in the payment notes for audit purposes.
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {showRejectForm ? (
            <>
              <Button
                variant="outline"
                onClick={() => setShowRejectForm(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={loading || !rejectReason.trim()}
                className="gap-2"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                Confirm Rejection
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setShowRejectForm(true)}
                disabled={loading}
                className="gap-2 text-destructive hover:text-destructive"
              >
                <XCircle className="h-4 w-4" />
                Reject Payment
              </Button>
              <Button
                onClick={handleApprove}
                disabled={loading}
                className="gap-2"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                Approve Payment
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ZelleVerificationDialog;